import { useContext } from 'react'

import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { QuizContext } from '../../context/QuizContext'
import QuestionHeading from '../../partials/QuestionHeading'
import { QuizStep } from '../../partials/types'
import { SliderElement } from './components'

type FinancialMeansSchema = {
  equity: number
  household_income: number
}

interface SliderElementProps {
  name: string
  label: string
  min: number
  max: number
  step: number
  default: number
  valueSuffix: string
  valuePrefix: string
  stepId: string
}

interface StepConfig {
  heading: string
  subheading: string
  firstSlider: SliderElementProps
  secondSlider: SliderElementProps
}

export const QuestionFinancialMeans = ({ step }: { step: QuizStep }) => {
  const context = useContext(QuizContext)
  const expectedValue = context?.data?.steps
    ?.find((s: QuizStep) => s.id === 'expected_value')
    ?.values?.reduce((acc, v) => {
      if (v.value) {
        return acc + v.value
      }
      return acc
    }, 0)

  const loanPurpose = context?.data?.steps?.find(
    (s: QuizStep) => s.id === 'loan_purpose'
  )?.values?.[0]?.value
  const propertyCosts = context?.data?.steps
    ?.find((s: QuizStep) => s.id === 'expected_value')
    ?.values?.find(v => v.name === 'property_costs')?.value
  const purchasePrice = context?.data?.steps
    ?.find((s: QuizStep) => s.id === 'expected_value')
    ?.values?.find(v => v.name === 'purchase_price')?.value

  const realEstateTransferTax = 0.06
  const brokerageFee = 0.0357
  const notaryFee = 0.02

  const totalFee =
    (realEstateTransferTax * 1000 + brokerageFee * 1000 + notaryFee * 1000) /
    1000

  function calculateAdditonalCosts() {
    if (loanPurpose) {
      if (loanPurpose === 'building_project') {
        return propertyCosts * totalFee
      }

      return purchasePrice * totalFee
    }
    return propertyCosts * totalFee
  }

  const additionalCosts = calculateAdditonalCosts()

  const stepConfigForFinancialMeans: StepConfig = {
    heading:
      'Welche Annahmen sollen wir Ihrem Finanzierungsangebot zugrunde legen?',
    subheading: '',
    firstSlider: {
      name: 'equity',
      min: additionalCosts,
      default: expectedValue * 0.2 > 600_000 ? 600_000 : expectedValue * 0.2,
      max: 600_000,
      step: 50_000,
      valuePrefix: '',
      valueSuffix: '€',
      label: 'Eigenkapital das Sie einbringen möchten',
      stepId: step.id
    },
    secondSlider: {
      name: 'household_income',
      min: 2_000,
      default: 3_500,
      max: 12_000,
      valuePrefix: '',
      valueSuffix: '€',
      step: 500,
      stepId: step.id,
      label: 'Monatliches Netto-Haushaltseinkommen'
    }
  }

  const stepConfig = stepConfigForFinancialMeans

  const defaultValues = {
    [stepConfig.firstSlider.name]: stepConfig.firstSlider.default,
    [stepConfig.secondSlider.name]: stepConfig.secondSlider.default
  }

  const financialMeansSchema: yup.ObjectSchema<FinancialMeansSchema> = yup.object(
    {
      equity: yup
        .number()
        .required()
        .min(stepConfig.firstSlider.min)
        .max(stepConfig.firstSlider.max),
      household_income: yup
        .number()
        .required()
        .min(stepConfig.secondSlider.min)
        .max(stepConfig.secondSlider.max)
    }
  )

  return (
    <>
      <QuestionHeading heading={step.heading} subheading={step.subheading} />
      <div className='[ f-wrap ]'>
        <Form.Wrapper
          onSubmit={() => {
            // Submission happens in SliderElement
          }}
          defaultValues={defaultValues}
          validationSchema={financialMeansSchema}>
          <SliderElement
            stepId={step.id}
            name={stepConfig.firstSlider.name}
            label={stepConfig.firstSlider.label}
            minValue={stepConfig.firstSlider.min}
            maxValue={stepConfig.firstSlider.max}
            valueStep={stepConfig.firstSlider.step}
            valueSuffix='€'
            valuePrefix=''
          />
          <SliderElement
            stepId={step.id}
            name={stepConfig.secondSlider.name}
            label={stepConfig.secondSlider.label}
            minValue={stepConfig.secondSlider.min}
            maxValue={stepConfig.secondSlider.max}
            valueStep={stepConfig.secondSlider.step}
            valueSuffix='€'
            valuePrefix=''
          />
        </Form.Wrapper>
      </div>
    </>
  )
}
